import React from 'react';
import { createPortal } from 'react-dom';
import { ClientContentState, ClientContentTopic, ClientPlanCatalogue, ClientPlanCatalogueNode, ClientPlanOverride, SubscribeClientContent } from '../interfaces/Frameworks';
import { getFirstFirestoreTimestampDate } from '../firestoreTimestamp';
import { Theme } from '../Theme';
import { formatClientContentError } from '../clientContent';
import { CLIENT_PLAN_MAX_TOPICS, collectClientPlanTopics } from '../clientContentCatalogue';
import { Button } from './Button';
import './ClientContentPanel.css';

const EMPTY_CONTENT_STATE: ClientContentState = { content: null, loading: false };

// Keep content scoped to the selected person, including while switching tabs or accounts.
export function useClientContent(options: {
    clientId: string;
    providerId: string;
    userId: string;
    enabled: boolean;
    subscribe?: SubscribeClientContent;
}) {
    const { clientId, providerId, userId, enabled, subscribe } = options;
    const scope = JSON.stringify([clientId, providerId, userId, enabled]);
    const [snapshot, setSnapshot] = React.useState({ scope, state: EMPTY_CONTENT_STATE, hasPublishedContent: false });
    const [retryCount, setRetryCount] = React.useState(0);
    const subscribeRef = React.useRef(subscribe);
    subscribeRef.current = subscribe;
    const canSubscribe = enabled && !!clientId && !!subscribe;

    React.useEffect(() => {
        let active = true;
        let unsubscribe: (() => void) | undefined;
        setSnapshot((previous) => ({
            scope, state: { content: null, loading: canSubscribe },
            hasPublishedContent: previous.scope === scope && previous.hasPublishedContent,
        }));
        if (canSubscribe) {
            try {
                unsubscribe = subscribeRef.current?.(clientId, (state) => {
                    // Late listener callbacks cannot restore another person's content.
                    if (active) setSnapshot((previous) => ({
                        scope, state,
                        hasPublishedContent: !!state.content || (previous.scope === scope && previous.hasPublishedContent),
                    }));
                });
            } catch (error) {
                setSnapshot((previous) => ({ scope, hasPublishedContent: previous.scope === scope && previous.hasPublishedContent, state: {
                    content: null, loading: false,
                    error: formatClientContentError(error),
                } }));
            }
        }
        return () => {
            active = false;
            unsubscribe?.();
        };
    }, [clientId, scope, canSubscribe, retryCount]);

    return {
        state: snapshot.scope === scope && enabled ? snapshot.state : EMPTY_CONTENT_STATE,
        hasPublishedContent: snapshot.scope === scope && enabled && snapshot.hasPublishedContent,
        retry: () => setRetryCount((count) => count + 1),
    };
}

// Compare calendar dates in the app's timezone, with a local fallback for older snapshots.
function getCalendarDate(now: Date, timeZone?: string): string {
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            year: 'numeric', month: '2-digit', day: '2-digit', timeZone,
        }).formatToParts(now);
        return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type)?.value).join('-');
    } catch {
        return getCalendarDate(now);
    }
}

// Calendar-only dates must not shift when the therapist views them from another timezone.
function formatPlanDate(date: string): string {
    const parsed = new Date(`${date}T12:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
}

// Preserve a useful topic icon when an older app omitted artwork or an image stops loading.
function TopicIcon({ topic }: { topic: ClientContentTopic }) {
    const [failedUrl, setFailedUrl] = React.useState('');
    let iconUrl = '';
    // Only web artwork URLs are displayable; app-local filenames use the fallback icon.
    try {
        const parsedUrl = new URL(topic.iconUrl || '');
        if (parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') iconUrl = parsedUrl.href;
    } catch { /* Missing or malformed artwork uses the topic fallback. */ }
    return (
        <div style={styles.iconContainer} aria-hidden='true'>
            {iconUrl && failedUrl !== iconUrl ? (
                <img src={iconUrl} alt='' style={styles.iconImage} onError={() => setFailedUrl(iconUrl)} />
            ) : (
                <svg viewBox='0 0 32 32' width='32' height='32' fill='none' stroke='currentColor' strokeWidth='1.7'>
                    <path d='M16 8c-4-3-8-3-12-2v19c4-1 8-1 12 2 4-3 8-3 12-2V6c-4-1-8-1-12 2Z' />
                    <path d='M16 8v19M8 11l4 1M8 16l4 1M20 12l4-1M20 17l4-1' />
                </svg>
            )}
        </div>
    );
}

// Symmetrical SVGs keep tile actions centred independently of font glyph metrics.
function PlanActionIcon({ action }: { action: 'up' | 'down' | 'remove' }) {
    const paths = {
        up: 'M4 10 8 6 12 10',
        down: 'M4 6 8 10 12 6',
        remove: 'M4 4 12 12M12 4 4 12',
    };
    return (
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='currentColor'
            strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
            <path d={paths[action]} />
        </svg>
    );
}

// Wide, read-only tiles mirror the app's topics without offering content actions.
function TopicTile({ topic, completed }: { topic: ClientContentTopic; completed: boolean }) {
    return (
        <li style={styles.topicTile}>
            <TopicIcon topic={topic} />
            <div style={styles.topicDetails}>
                <h3 style={styles.topicTitle}>{topic.title}</h3>
                <span style={{ ...styles.completion, ...(completed ? styles.completed : styles.pending) }}>
                    <span aria-hidden='true'>{completed ? '✓' : '○'}</span>
                    {completed ? 'Completed' : 'To complete'}
                </span>
            </div>
        </li>
    );
}

// Keep drag payloads local to this editor; text dragged from another page cannot create goals.
type PlanDrag = { kind: 'catalogue'; node: ClientPlanCatalogueNode } | { kind: 'draft'; id: string };

// Preserve the original order while avoiding duplicate assignments and completed history.
function remainingTopics(topics: ClientContentTopic[], completedIds: Set<string>): ClientContentTopic[] {
    const seen = new Set<string>();
    return topics.filter((topic) => {
        if (seen.has(topic.id) || completedIds.has(topic.id)) return false;
        seen.add(topic.id);
        return true;
    }).map((topic) => ({ ...topic, completed: false }));
}

// Explain failures without losing the therapist's work or exposing Firebase implementation details.
function editorError(error: unknown, action: 'load' | 'submit' | 'generate'): string {
    const details = error as { code?: string; message?: string } | null;
    const code = typeof details?.code === 'string' ? details.code.replace(/^(firestore|functions)\//, '') : '';
    if (code === 'permission-denied') return 'Access to customise this client’s plan was denied. Ask your service administrator to check content access.';
    if (code === 'unauthenticated') return 'Please sign in again before customising this client’s plan.';
    if (code === 'unavailable' || code === 'deadline-exceeded') return 'The content service is unavailable. Check your connection and try again.';
    if (action === 'generate') return details?.message || 'Could not generate a plan. Your instructions have been kept; please try again.';
    return details?.message || (action === 'load' ? 'Could not load available topics. Please try again.' : 'Could not submit the custom plan. Your edits have been kept; please try again.');
}

// Show the shared plan and history on the left, with an optional catalogue and draft editor.
export function ClientContentPanel({ state, onRetry, loadPlanCatalogue, onSubmitPlan, onGeneratePlan, headerActionContainer }: {
    state: ClientContentState;
    onRetry?: () => void;
    loadPlanCatalogue?: () => Promise<ClientPlanCatalogue>;
    onSubmitPlan?: (goalIds: string[]) => Promise<ClientPlanOverride>;
    onGeneratePlan?: (instructions: string) => Promise<{ goalIds: string[]; warnings?: string[] }>;
    headerActionContainer?: HTMLElement | null;
}) {
    const [now, setNow] = React.useState(() => new Date());
    const [editing, setEditing] = React.useState(false);
    const [catalogue, setCatalogue] = React.useState<ClientPlanCatalogue | null>(null);
    const [catalogueLoading, setCatalogueLoading] = React.useState(false);
    const [catalogueError, setCatalogueError] = React.useState('');
    const [path, setPath] = React.useState<ClientPlanCatalogueNode[]>([]);
    const [draft, setDraft] = React.useState<ClientContentTopic[]>([]);
    const [editNotice, setEditNotice] = React.useState('');
    const [submitError, setSubmitError] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [success, setSuccess] = React.useState('');
    const [generationFormOpen, setGenerationFormOpen] = React.useState(false);
    const [instructions, setInstructions] = React.useState('');
    const [generating, setGenerating] = React.useState(false);
    const [generationError, setGenerationError] = React.useState('');
    const [generationNotice, setGenerationNotice] = React.useState('');
    const [generatedWarnings, setGeneratedWarnings] = React.useState<string[]>([]);
    const generationInFlight = React.useRef(false);
    const instructionsId = React.useId();
    const [dragging, setDragging] = React.useState(false);
    const drag = React.useRef<PlanDrag | null>(null);
    const request = React.useRef(0);
    React.useEffect(() => {
        // An open tab must stop calling yesterday's unchanged plan "today" after midnight.
        const timer = window.setInterval(() => setNow(new Date()), 60_000);
        return () => { window.clearInterval(timer); request.current += 1; };
    }, []);
    const { content, loading, error } = state;
    const plan = content?.dailyPlan;
    // Either completion signal moves a goal into history once, retaining available tile artwork.
    const completedById = new Map((content?.completedTopics || []).map((topic) => [topic.id, topic]));
    (plan?.topics || []).forEach((topic) => {
        const historicalTopic = completedById.get(topic.id);
        if (topic.completed || historicalTopic) completedById.set(topic.id, { ...topic, ...historicalTopic, completed: true });
    });
    const completedTopics = Array.from(completedById.values());
    const completedIds = React.useRef(new Set<string>());
    completedIds.current = new Set(completedById.keys());
    const completionKey = JSON.stringify(Array.from(completedById.keys()).sort());
    const pendingTopics = remainingTopics(plan?.topics || [], completedIds.current);
    const isToday = !!plan && plan.date === getCalendarDate(now, content?.timeZone);
    const updatedAt = getFirstFirestoreTimestampDate(content?.updatedAt);
    const canEdit = !!loadPlanCatalogue && !!onSubmitPlan;
    const canGenerate = !!onGeneratePlan;
    React.useEffect(() => {
        // Revoke pending suggestions if generation becomes unavailable while the panel stays mounted.
        if (!canGenerate) {
            if (generationInFlight.current) request.current += 1;
            generationInFlight.current = false;
            setGenerating(false);
            setGenerationFormOpen(false);
            setGenerationError('');
            setGenerationNotice('');
        }
    }, [canGenerate]);
    const draftLocked = catalogueLoading || submitting || !catalogue || !!error || loading;
    const availableIds = new Set(catalogue?.topics.map((topic) => topic.id));
    const draftIds = new Set(draft.map((topic) => topic.id));
    const unavailableTopics = draft.filter((topic) => !availableIds.has(topic.id));
    const canSubmit = !draftLocked && draft.length > 0 && draft.length <= CLIENT_PLAN_MAX_TOPICS && !unavailableTopics.length;
    const visibleNodes = path.length ? path[path.length - 1].children : catalogue?.nodes || [];
    const levelLabel = path.length === 0 ? 'Support areas' : path.length === 1 ? 'Conditions' : path.length === 2 ? 'Categories' : 'Topics';

    React.useEffect(() => {
        // Live app completions leave the draft without replacing any other therapist edits.
        setDraft((previous) => {
            const next = previous.filter((topic) => !completedIds.current.has(topic.id));
            return next.length === previous.length ? previous : next;
        });
    }, [completionKey]);

    // Read the latest assignment on every edit so a plan awaiting app sync is not overwritten accidentally.
    async function loadCatalogue() {
        if (!loadPlanCatalogue) return;
        const currentRequest = ++request.current;
        setCatalogueLoading(true);
        setCatalogueError('');
        try {
            const next = await loadPlanCatalogue();
            if (currentRequest !== request.current) return;
            const byId = new Map([...pendingTopics, ...next.topics].map((topic) => [topic.id, topic]));
            const seed = next.activeOverride ? next.activeOverride.goalIds.map((id) => byId.get(id) || {
                id, title: `Unavailable topic (${id})`, completed: false,
            }) : pendingTopics;
            setCatalogue(next);
            setDraft(remainingTopics(seed, completedIds.current));
            setPath([]);
        } catch (failure) {
            if (currentRequest === request.current) setCatalogueError(editorError(failure, 'load'));
        } finally {
            if (currentRequest === request.current) setCatalogueLoading(false);
        }
    }

    // Seed a draft immediately, then reconcile it against the current database assignment.
    function startEditing() {
        if (generationFormOpen || generationInFlight.current) return;
        setEditing(true);
        setDraft(pendingTopics);
        setCatalogue(null);
        setEditNotice('');
        setSubmitError('');
        setSuccess('');
        setGeneratedWarnings([]);
        setPath([]);
        void loadCatalogue();
    }

    // Collect optional guidance before requesting a suggestion for the therapist to review.
    function openGenerationForm() {
        if (editing || generationInFlight.current) return;
        setGenerationFormOpen(true);
        setGenerationError('');
        setGenerationNotice('');
        setSuccess('');
    }

    // Cancellation ignores an outstanding suggestion without changing the client's assigned plan.
    function cancelGeneration() {
        request.current += 1;
        generationInFlight.current = false;
        setGenerating(false);
        setGenerationFormOpen(false);
        setGenerationError('');
        setGenerationNotice('');
    }

    // Resolve generated IDs against a fresh catalogue and current completions before entering edit mode.
    async function generatePlan() {
        if (!onGeneratePlan || !loadPlanCatalogue || editing || generationInFlight.current || loading || error) return;
        const currentRequest = ++request.current;
        generationInFlight.current = true;
        setGenerating(true);
        setGenerationError('');
        setGenerationNotice('');
        try {
            const result = await onGeneratePlan(instructions.trim());
            if (currentRequest !== request.current) return;
            const nextCatalogue = await loadPlanCatalogue();
            if (currentRequest !== request.current) return;
            const byId = new Map(nextCatalogue.topics.map((topic) => [topic.id, topic]));
            const suggestedIds = Array.from(new Set(result.goalIds));
            const suggestions = suggestedIds.flatMap((id) => byId.has(id) ? [byId.get(id)!] : []);
            const nextDraft = remainingTopics(suggestions, completedIds.current);
            const warnings = [...(result.warnings || [])];
            if (suggestedIds.some((id) => !byId.has(id))) warnings.push('Some suggested topics are no longer available and were left out of this draft.');
            if (suggestedIds.some((id) => completedIds.current.has(id))) warnings.push('Topics completed while the plan was being generated were left out of this draft.');
            if (!nextDraft.length) {
                setGenerationNotice(['No new topics were suggested. Adjust your instructions and try again, or cancel to customise the plan manually.', ...warnings].join(' '));
                return;
            }
            setCatalogue(nextCatalogue);
            setCatalogueError('');
            setCatalogueLoading(false);
            setDraft(nextDraft);
            setPath([]);
            setEditNotice('Review and edit the suggested topics, then Submit to assign this plan to the client.');
            setGeneratedWarnings(warnings);
            setSubmitError('');
            setGenerationFormOpen(false);
            setEditing(true);
        } catch (failure) {
            if (currentRequest === request.current) setGenerationError(editorError(failure, 'generate'));
        } finally {
            if (currentRequest === request.current) {
                generationInFlight.current = false;
                setGenerating(false);
            }
        }
    }

    // Expand whole groups, keeping over-limit drafts editable until the therapist chooses what to remove.
    function addTopics(topics: ClientContentTopic[], index = draft.length) {
        if (draftLocked) return;
        const existing = new Set(draft.map((topic) => topic.id));
        const additions = remainingTopics(topics, completedIds.current).filter((topic) => !existing.has(topic.id));
        if (!additions.length) {
            setEditNotice('These topics are already in the plan or have been completed.');
            return;
        }
        setDraft([...draft.slice(0, index), ...additions, ...draft.slice(index)]);
        setSubmitError('');
        setEditNotice(`${additions.length} ${additions.length === 1 ? 'topic added' : 'topics added'} to the custom plan.${additions.length < topics.length ? ' Completed topics and duplicates were skipped.' : ''}${draft.length + additions.length > CLIENT_PLAN_MAX_TOPICS ? ` Remove topics to reach the ${CLIENT_PLAN_MAX_TOPICS}-topic limit before submitting.` : ''}`);
    }

    // Moving to a drop position preserves the relative order of every other topic.
    function moveTopic(id: string, target: number) {
        if (draftLocked) return;
        const source = draft.findIndex((topic) => topic.id === id);
        if (source < 0) return;
        const next = [...draft];
        const [topic] = next.splice(source, 1);
        next.splice(source < target ? target - 1 : target, 0, topic);
        setDraft(next);
        setSubmitError('');
        setEditNotice('Topic order updated.');
    }

    // Expose accessible buttons alongside native drag and drop for keyboard and touch users.
    function removeTopic(id: string) {
        if (draftLocked) return;
        setDraft(draft.filter((topic) => topic.id !== id));
        setSubmitError('');
        setEditNotice('Topic removed from the custom plan.');
    }

    // Native drag data only identifies the operation; trusted topic objects remain in this component.
    function startDrag(event: React.DragEvent, payload: PlanDrag) {
        if (draftLocked) { event.preventDefault(); return; }
        drag.current = payload;
        event.dataTransfer.effectAllowed = payload.kind === 'draft' ? 'move' : 'copy';
        event.dataTransfer.setData('application/x-superpenguin-plan', payload.kind);
        setDragging(true);
    }

    // Drop on a tile to insert before it, or on the surrounding plan to append.
    function dropTopics(event: React.DragEvent, index: number) {
        event.preventDefault();
        event.stopPropagation();
        const payload = drag.current;
        drag.current = null;
        setDragging(false);
        if (!payload || draftLocked) return;
        if (payload.kind === 'draft') moveTopic(payload.id, index);
        else addTopics(collectClientPlanTopics(payload.node), index);
    }

    // A successful database write closes the editor; a failed write leaves the exact draft available.
    async function submitPlan() {
        if (!canSubmit || !onSubmitPlan || submitting) return;
        const currentRequest = ++request.current;
        setSubmitting(true);
        setSubmitError('');
        try {
            await onSubmitPlan(draft.map((topic) => topic.id));
            if (currentRequest !== request.current) return;
            setEditing(false);
            setSuccess('Custom plan submitted. It will appear in SuperPenguin when the app syncs, and remain until all its topics are completed.');
        } catch (failure) {
            if (currentRequest === request.current) setSubmitError(editorError(failure, 'submit'));
        } finally {
            if (currentRequest === request.current) setSubmitting(false);
        }
    }

    // Discard is local only; it never deletes or changes an already assigned database plan.
    function discard() {
        if (submitting) return;
        request.current += 1;
        setEditing(false);
        setDraft([]);
        setCatalogueLoading(false);
        setGeneratedWarnings([]);
        drag.current = null;
        setDragging(false);
    }

    // Keep the action tied to editor state while placing it in the record's active-tab header.
    const headerActions = canEdit && !editing ? <div className='client-content-header-actions'>
        {onGeneratePlan && <Button label='Generate plan' type='secondary' disabled={loading || !!error || generationFormOpen}
            onClick={openGenerationForm} style={{ flexShrink: 0 }} />}
        <Button label='Customise the plan' type='secondary'
            disabled={loading || !!error || generationFormOpen} onClick={startEditing} style={{ flexShrink: 0 }} />
    </div> : null;

    return (
        <div className='client-content-panel'>
            {headerActionContainer && createPortal(headerActions, headerActionContainer)}
            <div className='client-content-toolbar'>
                <div>
                    <p style={styles.description}>Planned and completed topics shared from the client’s SuperPenguin app.</p>
                    {updatedAt && <p className='client-content-updated'>Last shared {updatedAt.toLocaleString()}</p>}
                </div>
                {/* Standalone panel previews can still render their action without a record header. */}
                {headerActionContainer === undefined && headerActions}
            </div>
            {success && <p role='status' className='client-content-success'>{success}</p>}
            {generationFormOpen && <form aria-label='Generate a custom plan' className='client-content-generation-form'
                onSubmit={(event) => { event.preventDefault(); void generatePlan(); }}>
                <h2 style={styles.heading}>Generate a plan</h2>
                <p style={styles.notice}>Suggest topics using the client’s assessment results, briefing and completed topics. You can edit the suggestion before submitting it.</p>
                <label htmlFor={instructionsId}>Plan instructions (optional)</label>
                <textarea id={instructionsId} value={instructions} maxLength={4000} rows={4} disabled={generating}
                    placeholder='For example, focus on family support at home'
                    onChange={(event) => setInstructions(event.target.value)} />
                {generating && <p role='status' style={styles.notice}>Generating a suggested plan…</p>}
                {generationError && <p role='alert' style={styles.error}>{generationError}</p>}
                {generationNotice && <p role='status' style={styles.notice}>{generationNotice}</p>}
                <div className='client-content-editor-actions'>
                    <Button label={generating ? 'Generating…' : 'Generate'} type='primary' htmlType='submit'
                        disabled={generating || loading || !!error || instructions.length > 4000} />
                    <Button label='Cancel' type='secondary' onClick={cancelGeneration} />
                </div>
            </form>}
            <div className='client-content-layout'>
                <div className='client-content-left'>
                    {loading && <p role='status' style={styles.notice}>Loading SuperPenguin content…</p>}
                    {error && <div role='alert' style={styles.error}><span>{error}</span>{onRetry && <button type='button' onClick={onRetry} style={styles.retryButton}>Retry content</button>}</div>}
                    {!loading && !error && !content && !editing && <p role='status' style={styles.notice}>No content has been shared yet. Daily plans and completed topics will appear when the client next uses an updated version of SuperPenguin.</p>}
                    {editing ? (
                        <section aria-label='Custom plan' className='client-content-draft' data-dragging={dragging || undefined}
                            onDragOver={(event) => { if (drag.current && !draftLocked) event.preventDefault(); }}
                            onDrop={(event) => dropTopics(event, draft.length)}>
                            <div style={styles.sectionHeader}><h2 style={styles.heading}>Custom plan</h2><span style={styles.count}>{draft.length} / {CLIENT_PLAN_MAX_TOPICS}</span></div>
                            <p style={styles.notice}>Drag topics here, or use Add. Reorder topics with the arrow buttons or drag them above another topic.</p>
                            {!draft.length && <p className='client-content-drop-empty'>Add topics from the catalogue to build the client’s plan.</p>}
                            {/* Keep drop targets in place so starting a drag does not shift the draft. */}
                            {draft.length > 0 && <div className='client-content-insertion-target' onDrop={(event) => dropTopics(event, 0)}>Drop here to add at the start</div>}
                            <ol aria-label='Custom plan topics' style={styles.topicList}>
                                {draft.map((topic, index) => (
                                    <li key={topic.id} className='client-content-draft-tile' draggable={!draftLocked}
                                        onDragStart={(event) => startDrag(event, { kind: 'draft', id: topic.id })}
                                        onDragEnd={() => { drag.current = null; setDragging(false); }}
                                        onDragOver={(event) => { if (drag.current && !draftLocked) event.preventDefault(); }}
                                        onDrop={(event) => dropTopics(event, index)}>
                                        <span className='client-content-order' aria-hidden='true'>{index + 1}</span>
                                        <TopicIcon topic={topic} />
                                        <div style={styles.topicDetails}><h3 style={styles.topicTitle}>{topic.title}</h3><span className='client-content-tile-state'>{catalogue && !availableIds.has(topic.id) ? 'Unavailable — remove before submitting' : 'To complete'}</span></div>
                                        <div className='client-content-tile-actions'>
                                            <button type='button' aria-label={`Move ${topic.title} up`} disabled={draftLocked || index === 0} onClick={() => moveTopic(topic.id, index - 1)}><PlanActionIcon action='up' /></button>
                                            <button type='button' aria-label={`Move ${topic.title} down`} disabled={draftLocked || index === draft.length - 1} onClick={() => moveTopic(topic.id, index + 2)}><PlanActionIcon action='down' /></button>
                                            <button type='button' aria-label={`Remove ${topic.title}`} disabled={draftLocked} onClick={() => removeTopic(topic.id)}><PlanActionIcon action='remove' /></button>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                            {draft.length > 0 && <div className='client-content-insertion-target' onDrop={(event) => dropTopics(event, draft.length)}>Drop topics here to add at the end</div>}
                            {editNotice && <p role='status' className='client-content-edit-notice'>{editNotice}</p>}
                            {generatedWarnings.length > 0 && <div role='status' className='client-content-generation-warnings'>
                                {generatedWarnings.map((warning, index) => <p key={index}>{warning}</p>)}
                            </div>}
                            {catalogue && unavailableTopics.length > 0 && <p role='alert' style={styles.notice}>Some assigned topics are no longer available in the catalogue. Remove or replace them before submitting.</p>}
                            {draft.length > CLIENT_PLAN_MAX_TOPICS && <p role='alert' style={styles.notice}>Remove topics until the plan contains at most {CLIENT_PLAN_MAX_TOPICS} topics.</p>}
                            {submitError && <p role='alert' style={styles.error}>{submitError}</p>}
                            <div className='client-content-editor-actions'>
                                <button type='button' className='client-content-button primary' disabled={!canSubmit || submitting} onClick={() => void submitPlan()}>{submitting ? 'Submitting…' : 'Submit'}</button>
                                <button type='button' className='client-content-button' disabled={submitting} onClick={discard}>Discard</button>
                            </div>
                            <p className='client-content-edit-footnote'>Submitting replaces the client’s assigned plan. The AI plan resumes after all assigned topics are completed.</p>
                        </section>
                    ) : content && (
                        <section aria-label={isToday ? 'Today’s daily plan' : 'Latest daily plan'}>
                            <div style={styles.sectionHeader}><h2 style={styles.heading}>{isToday ? 'Today’s daily plan' : 'Latest daily plan'}</h2>{plan && <span style={styles.planDate}>{formatPlanDate(plan.date)}</span>}</div>
                            {plan && !isToday && <p style={styles.notice}>This is the latest plan shared by the app. No plan for today has been shared.</p>}
                            {pendingTopics.length > 0 ? <ul style={styles.topicList}>{pendingTopics.map((topic) => <TopicTile key={topic.id} topic={topic} completed={false} />)}</ul> :
                                <p style={styles.notice}>{!plan ? 'No daily plan has been shared yet.' : plan.topics.length > 0 ? 'All topics in this daily plan are completed.' : 'No topics in this daily plan.'}</p>}
                        </section>
                    )}
                    {(content || editing) && <section aria-label='Completed topics'>
                        <div style={styles.sectionHeader}><h2 style={styles.heading}>Completed topics</h2><span style={styles.count}>{completedTopics.length}</span></div>
                        {completedTopics.length > 0 ? <ul style={styles.topicList}>{completedTopics.map((topic) => <TopicTile key={topic.id} topic={topic} completed />)}</ul> : <p style={styles.notice}>No completed topics have been shared yet.</p>}
                    </section>}
                </div>
                {editing && <section aria-label='Available topics' className='client-content-catalogue'>
                    <h2 style={styles.heading}>Available topics</h2>
                    <p style={styles.notice}>Browse support areas, conditions and categories. Drag a condition or category to add all its unfinished topics.</p>
                    {catalogueLoading && <p role='status' style={styles.notice}>Loading available topics…</p>}
                    {catalogueError && <div role='alert' style={styles.error}><span>{catalogueError}</span><button type='button' className='client-content-button' onClick={() => void loadCatalogue()}>Retry topics</button></div>}
                    {catalogue && !catalogueLoading && <>
                        <nav aria-label='Topic catalogue navigation' className='client-content-breadcrumbs'>
                            <button type='button' disabled={path.length === 0} onClick={() => setPath([])}>Support areas</button>
                            {path.map((node, index) => <React.Fragment key={node.id}><span aria-hidden='true'>›</span><button type='button' disabled={index === path.length - 1} onClick={() => setPath(path.slice(0, index + 1))}>{node.title}</button></React.Fragment>)}
                        </nav>
                        <div className='client-content-catalogue-heading'><h3>{levelLabel}</h3>{path.length > 0 && <button type='button' className='client-content-button' onClick={() => setPath(path.slice(0, -1))}>← Back</button>}</div>
                        {visibleNodes.length ? <ul className='client-content-catalogue-list' aria-label={levelLabel}>
                            {visibleNodes.map((node) => {
                                const topicCompleted = node.kind === 'topic' && completedIds.current.has(node.topic!.id);
                                const inPlan = node.kind === 'topic' && draftIds.has(node.topic!.id);
                                const remainingCount = node.topicIds.filter((id) => !completedIds.current.has(id) && !draftIds.has(id)).length;
                                const addable = node.kind !== 'speechNeed' && remainingCount > 0;
                                return <li key={node.id} className='client-content-catalogue-tile' draggable={!draftLocked && addable}
                                    onDragStart={(event) => startDrag(event, { kind: 'catalogue', node })}
                                    onDragEnd={() => { drag.current = null; setDragging(false); }}>
                                    {node.topic ? <TopicIcon topic={node.topic} /> : <span className='client-content-folder' aria-hidden='true'>▱</span>}
                                    <div className='client-content-catalogue-details'>
                                        {node.kind === 'topic' ? <h4>{node.title}</h4> : <button type='button' className='client-content-drill' onClick={() => setPath([...path, node])} aria-label={`Browse ${node.title}`}>{node.title}<span aria-hidden='true'>›</span></button>}
                                        <span>{topicCompleted ? 'Completed' : inPlan ? 'In custom plan' : node.kind === 'topic' ? 'Topic' : `${node.topicIds.length} ${node.topicIds.length === 1 ? 'topic' : 'topics'} · ${remainingCount} to add`}{node.topic?.language ? ` · ${node.topic.language}` : ''}</span>
                                    </div>
                                    {node.kind !== 'speechNeed' && <button type='button' className='client-content-button small' disabled={draftLocked || !addable} aria-label={`Add ${node.title}`} onClick={() => addTopics(collectClientPlanTopics(node))}>{topicCompleted ? 'Completed' : inPlan ? 'Added' : 'Add'}</button>}
                                </li>;
                            })}
                        </ul> : <p style={styles.notice}>No available topics in this part of the catalogue.</p>}
                    </>}
                </section>}
            </div>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    description: { margin: 0, color: '#595963', fontSize: '14px', lineHeight: 1.6 },
    sectionHeader: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' },
    heading: { margin: 0, fontSize: '20px', fontWeight: 600, color: Theme.Colours.text },
    planDate: { fontSize: '13px', color: '#666672' },
    count: { background: '#edeaf8', color: '#504985', borderRadius: '20px', padding: '3px 10px', fontSize: '13px' },
    notice: { margin: '0 0 14px', color: '#666672', fontSize: '14px', lineHeight: 1.6 },
    topicList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' },
    topicTile: { display: 'flex', alignItems: 'center', gap: '18px', minHeight: '84px', padding: '14px 18px', border: '1px solid #e3e0f0', borderRadius: '12px', background: Theme.Colours.workArea },
    iconContainer: { width: '68px', height: '68px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', overflow: 'hidden', background: '#f0eefb', color: '#746bc2' },
    iconImage: { width: '100%', height: '100%', objectFit: 'cover' },
    topicDetails: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: '9px', alignItems: 'flex-start' },
    topicTitle: { margin: 0, fontSize: '16px', fontWeight: 600, lineHeight: 1.4, overflowWrap: 'anywhere' },
    completion: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, padding: '3px 9px', borderRadius: '20px' },
    completed: { color: '#28623d', background: '#edf7ef' },
    pending: { color: '#665999', background: '#f0edf8' },
    error: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '8px', background: '#fff2f0', color: '#912d29', fontSize: '14px' },
    retryButton: { padding: '7px 12px', border: '1px solid #cbb9b7', borderRadius: '5px', background: Theme.Colours.workArea, color: '#912d29', cursor: 'pointer' },
};
